/**
 * MKS-292 — how the kanban card words its "Blocked by" / "Blocks" line.
 *
 * The per-counterpart REASON wording is not reinvented here: it comes from
 * `heldByReason` in `robotHold.ts`, the same sentence the task activity timeline
 * and the Bot Admin held list already use ("same area: Time entry & timesheets").
 * The only thing added is the direction, because "blocks this task" reads
 * backwards on the card that is doing the blocking.
 */
import type { TaskBlockRef } from '../services/api/board';
import { heldByReason } from './robotHold';

export type BlockDirection = 'blockedBy' | 'blocks';

/** How many codes fit inline before the rest collapse into `+N`. */
const INLINE_CODE_LIMIT = 2;

/** `MKS-266`, or `#1234` for a legacy task that never got a code. */
export function blockRefLabel(ref: TaskBlockRef): string {
  return ref.code ?? `#${ref.taskId}`;
}

/**
 * The inline text after the icon: every code when they fit, otherwise the first
 * one plus `+N` for the rest (the full list stays in the tooltip).
 *   1 ref  -> `MKS-266`
 *   2 refs -> `MKS-266, MKS-279`
 *   3 refs -> `MKS-266 +2`
 */
export function formatBlockRefCodes(refs: readonly TaskBlockRef[]): string {
  const [first] = refs;
  if (!first) return '';
  if (refs.length <= INLINE_CODE_LIMIT) return refs.map(blockRefLabel).join(', ');
  return `${blockRefLabel(first)} +${refs.length - 1}`;
}

/**
 * Why this counterpart is on the line. An area overlap keeps `heldByReason`'s
 * wording verbatim; a pure link is phrased from the hovered card's point of
 * view. A pair that is both an area overlap AND a link reads as the area reason
 * — the link is then the less informative of the two.
 */
export function blockRefReason(ref: TaskBlockRef, direction: BlockDirection): string {
  if (ref.areaLabels.length > 0) {
    return heldByReason({ code: blockRefLabel(ref), areaLabels: ref.areaLabels });
  }
  return direction === 'blockedBy' ? 'blocks this task' : 'blocked by this task';
}

/**
 * The `title` of the whole line: the heading, then one `CODE — reason` per
 * counterpart, including the ones hidden behind `+N`.
 */
export function blockRefsTooltip(
  refs: readonly TaskBlockRef[],
  direction: BlockDirection,
): string {
  const heading = direction === 'blockedBy' ? 'Blocked by' : 'Blocks';
  const lines = refs.map((ref) => `${blockRefLabel(ref)} — ${blockRefReason(ref, direction)}`);
  return [`${heading}:`, ...lines].join('\n');
}
